"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useId, useState } from "react";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type {
  HotelPromoHotel,
  HomepageHotelPromos,
} from "@/types/services/public/apis/Settings";
import type { BannerItem } from "@/types/services/public/apis/Banners";
import { getBannerLink } from "@/shared/libs/banner-utils";
import "swiper/css";
import "swiper/css/pagination";

interface LandingHotelPromosProps {
  data?: HomepageHotelPromos | null;
  heroCarousel?: BannerItem[];
  heroSideCarousel?: BannerItem[];
  serviceHighlights?: BannerItem[];
  destinationCards?: BannerItem[];
  destinationTitle?: string;
  destinationSlogan?: string;
  flightDestinationCards?: boolean;
  displayDiscountedHotels?: boolean;
  standaloneDestinationSection?: boolean;
  homepage?: boolean;
  device: DeviceType;
}

type HeroSlide = {
  id: string | number;
  title: string;
  image: string;
  href?: string;
  isExternal?: boolean;
  unoptimized?: boolean;
};

export default function LandingHotelPromos({
  data,
  heroCarousel,
  heroSideCarousel,
  serviceHighlights,
  destinationCards,
  destinationTitle,
  destinationSlogan,
  flightDestinationCards = false,
  displayDiscountedHotels = true,
  standaloneDestinationSection = false,
  homepage = false,
  device,
}: LandingHotelPromosProps) {
  const enableAvalSafarDiscountTest =
    process.env.NEXT_PUBLIC_ENABLE_AVALSAFAR_DISCOUNT_TEST === "true";
  const [testDiscountedHotels, setTestDiscountedHotels] = useState<
    HotelPromoHotel[]
  >([]);
  const cityPaginationId = useId().replaceAll(":", "-");
  const discountPaginationId = useId().replaceAll(":", "-");
  const cityPaginationClass = `city-hotels-pagination-${cityPaginationId}`;
  const discountPaginationClass = `discount-hotels-pagination-${discountPaginationId}`;

  const dynamicHeroSlides = (heroCarousel ?? [])
    .map((banner) => mapBannerToHeroSlide(banner, device))
    .filter((slide): slide is HeroSlide => Boolean(slide));
  const heroSlides = dynamicHeroSlides;
  const dynamicHeroSideSlides = (heroSideCarousel ?? [])
    .map((banner) => mapBannerToHeroSlide(banner, device))
    .filter((slide): slide is HeroSlide => Boolean(slide));
  const heroSideSlides = dynamicHeroSideSlides;
  const featureBanners = (serviceHighlights ?? [])
    .map((banner) => mapBannerToHeroSlide(banner, device))
    .filter((slide): slide is HeroSlide => Boolean(slide));
  const carouselGridClass = heroSideSlides.length
    ? homepage
      ? "grid gap-3 lg:grid-cols-2 lg:items-start"
      : "grid gap-3 lg:grid-cols-[minmax(0,12fr)_minmax(220px,5fr)] lg:items-start"
    : "grid gap-3";
  const cityHotelSlides = (destinationCards ?? [])
    .map((banner) => mapBannerToHeroSlide(banner, device))
    .filter((slide): slide is HeroSlide => Boolean(slide));
  const discountedHotels = enableAvalSafarDiscountTest
    ? testDiscountedHotels.length
      ? testDiscountedHotels
      : (data?.discounted_hotels ?? [])
    : (data?.discounted_hotels ?? []);
  const showDiscountedHotels =
    displayDiscountedHotels && discountedHotels.length > 0;

  useEffect(() => {
    if (!displayDiscountedHotels || !enableAvalSafarDiscountTest) return;

    fetch("/api/dev/avalsafar-discounted-hotels/", { cache: "no-store" })
      .then((response) => response.json())
      .then((response: { results?: HotelPromoHotel[] }) => {
        const validResults = response.results?.filter(
          (hotel) => hotel.image && !hotel.image.includes("empty-state"),
        );

        if (validResults?.length) setTestDiscountedHotels(validResults);
      })
      .catch(() => undefined);
  }, [displayDiscountedHotels, enableAvalSafarDiscountTest]);

  if (
    !heroSlides.length &&
    !heroSideSlides.length &&
    !featureBanners.length &&
    !cityHotelSlides.length &&
    (!displayDiscountedHotels || !discountedHotels.length)
  ) {
    return null;
  }

  return (
    <section
      className={
        standaloneDestinationSection
          ? "mb-8 md:mb-12"
          : "mb-8 translate-y-0 md:mb-12 md:-translate-y-4"
      }
    >
      {(heroSlides.length > 0 || heroSideSlides.length > 0) && (
        <div className={carouselGridClass}>
          {heroSlides.length > 0 && (
            <Swiper
              modules={[Autoplay, Pagination]}
              loop={heroSlides.length > 1}
              autoplay={
                heroSlides.length > 1
                  ? {
                      delay: 4500,
                      disableOnInteraction: false,
                    }
                  : false
              }
              pagination={{
                clickable: true,
              }}
              className="w-full overflow-hidden rounded-sm shadow-1"
            >
              {heroSlides.map((slide, index) => (
                <SwiperSlide key={slide.id}>
                  <HeroSlideContent slide={slide} index={index} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          {heroSideSlides.length > 0 && (
            <Swiper
              modules={[Autoplay, Pagination]}
              loop={heroSideSlides.length > 1}
              autoplay={
                heroSideSlides.length > 1
                  ? {
                      delay: 5200,
                      disableOnInteraction: false,
                    }
                  : false
              }
              pagination={{
                clickable: true,
              }}
              className="w-full overflow-hidden rounded-sm shadow-1"
            >
              {heroSideSlides.map((slide, index) => (
                <SwiperSlide key={slide.id}>
                  <HeroSideSlideContent
                    slide={slide}
                    index={index}
                    homepage={homepage}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      )}

      {showDiscountedHotels && discountedHotels.length > 0 && (
        <section className="mt-8 overflow-hidden rounded-sm border border-red-100 bg-red-50 p-4 md:mt-10 md:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-semibold text-red-600">
                پیشنهادهای داغ رزرو هتل
              </span>
              <h2 className="mt-1 text-xl font-bold text-foreground md:text-2xl">
                {data?.discounted_section_title ?? "هتل های پرتخفیف امروز"}
              </h2>
            </div>
            <OfferCountdown />
          </div>

          <Swiper
            modules={[Pagination]}
            loop={discountedHotels.length > 4}
            slidesPerView={2.5}
            spaceBetween={10}
            breakpoints={{
              640: { slidesPerView: 2.5, spaceBetween: 12 },
              1024: { slidesPerView: 4, spaceBetween: 16 },
              1280: { slidesPerView: 5, spaceBetween: 16 },
            }}
            pagination={{
              el: `.${discountPaginationClass}`,
              clickable: true,
            }}
          >
            {discountedHotels.map((hotel) => (
              <SwiperSlide key={hotel.id}>
                <DiscountHotelCard hotel={hotel} />
              </SwiperSlide>
            ))}
          </Swiper>
          <div className={`${discountPaginationClass} mt-3 text-center`} />
        </section>
      )}

      {featureBanners.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {featureBanners.map((banner) => (
            <FeatureBanner key={banner.id} banner={banner} />
          ))}
        </div>
      )}

      {cityHotelSlides.length > 0 && (
        <section className="mt-8 md:mt-10">
          <div className="mb-4">
            <h2 className="text-lg font-bold md:text-xl">
              {destinationTitle ??
                data?.city_section_title ??
                "هتل های شهرها"}
            </h2>
            {destinationSlogan && (
              <p className="mt-1 text-sm text-muted-foreground">
                {destinationSlogan}
              </p>
            )}
          </div>
          <Swiper
            modules={[Pagination]}
            loop={cityHotelSlides.length > 6}
            slidesPerView={2}
            spaceBetween={12}
            breakpoints={{
              768: { slidesPerView: 3, spaceBetween: 14 },
              1024: { slidesPerView: 4, spaceBetween: 16 },
              1280: { slidesPerView: 6, spaceBetween: 16 },
            }}
            pagination={{
              el: `.${cityPaginationClass}`,
              clickable: true,
            }}
          >
            {cityHotelSlides.map((slide, index) => (
              <SwiperSlide key={slide.id}>
                <DestinationCard
                  slide={slide}
                  index={index}
                  flightStyle={flightDestinationCards}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          <div className={`${cityPaginationClass} mt-2 text-center`} />
        </section>
      )}
    </section>
  );
}

function HeroSlideContent({
  slide,
  index,
}: {
  slide: HeroSlide;
  index: number;
}) {
  const image = (
    <Image
      src={slide.image}
      alt={slide.title}
      fill
      priority={index === 0}
      sizes="(max-width: 1024px) 100vw, 70vw"
      className="object-contain"
      unoptimized={slide.unoptimized}
    />
  );
  const className = "relative block aspect-[1680/700] w-full overflow-hidden";

  if (!slide.href) return <div className={className}>{image}</div>;

  if (slide.isExternal) {
    return (
      <a
        href={slide.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-analytics-event="banner_click"
        data-analytics-id={`homepage-hotel-hero-${slide.id}`}
        data-analytics-name={slide.title}
        data-analytics-type="link"
        data-analytics-funnel="hotel_booking"
        data-analytics-step="landing_hotel_promo"
        data-analytics-property-href={slide.href}
      >
        {image}
      </a>
    );
  }

  return (
    <Link
      href={slide.href}
      className={className}
      data-analytics-event="banner_click"
      data-analytics-id={`homepage-hotel-hero-${slide.id}`}
      data-analytics-name={slide.title}
      data-analytics-type="link"
      data-analytics-funnel="hotel_booking"
      data-analytics-step="landing_hotel_promo"
      data-analytics-property-href={slide.href}
    >
      {image}
    </Link>
  );
}

function HeroSideSlideContent({
  slide,
  index,
  homepage,
}: {
  slide: HeroSlide;
  index: number;
  homepage: boolean;
}) {
  const image = (
    <Image
      src={slide.image}
      alt={slide.title}
      fill
      priority={index === 0}
      sizes={homepage ? "(max-width: 1024px) 100vw, 50vw" : "(max-width: 1024px) 100vw, 30vw"}
      className="object-cover"
      unoptimized={slide.unoptimized}
    />
  );
  const className = homepage
    ? "relative block aspect-square overflow-hidden lg:aspect-[1680/700]"
    : "relative block aspect-square overflow-hidden";

  if (!slide.href) return <div className={className}>{image}</div>;

  if (slide.isExternal) {
    return (
      <a
        href={slide.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-analytics-event="banner_click"
        data-analytics-id={`homepage-hotel-side-${slide.id}`}
        data-analytics-name={slide.title}
        data-analytics-type="link"
        data-analytics-funnel="hotel_booking"
        data-analytics-step="landing_hotel_promo"
        data-analytics-property-href={slide.href}
      >
        {image}
      </a>
    );
  }

  return (
    <Link
      href={slide.href}
      className={className}
      data-analytics-event="banner_click"
      data-analytics-id={`homepage-hotel-side-${slide.id}`}
      data-analytics-name={slide.title}
      data-analytics-type="link"
      data-analytics-funnel="hotel_booking"
      data-analytics-step="landing_hotel_promo"
      data-analytics-property-href={slide.href}
    >
      {image}
    </Link>
  );
}

function DestinationCard({
  slide,
  index,
  flightStyle = false,
}: {
  slide: HeroSlide;
  index: number;
  flightStyle?: boolean;
}) {
  if (flightStyle) {
    const flightContent = (
      <article className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="relative aspect-[9/16] w-full overflow-hidden bg-muted">
          <Image
            src={slide.image}
            alt={slide.title}
            fill
            priority={index === 0}
            sizes="(max-width: 768px) 50vw, (max-width: 1280px) 25vw, 16vw"
            className="object-cover transition duration-300 group-hover:scale-105"
            unoptimized={slide.unoptimized}
          />
        </div>
        <h3 className="truncate px-3 py-3 text-center text-sm font-semibold">
          {slide.title}
        </h3>
      </article>
    );

    if (!slide.href) return <div>{flightContent}</div>;

    if (slide.isExternal) {
      return (
        <a
          href={slide.href}
          target="_blank"
          rel="noopener noreferrer"
          className="group block"
        >
          {flightContent}
        </a>
      );
    }

    return (
      <Link href={slide.href} className="group block">
        {flightContent}
      </Link>
    );
  }

  const content = (
    <>
      <div className="relative aspect-[9/16] w-full overflow-hidden rounded-sm">
        <Image
          src={slide.image}
          alt={slide.title}
          fill
          priority={index === 0}
          sizes="(max-width: 768px) 50vw, (max-width: 1280px) 25vw, 16vw"
          className="object-cover transition duration-300 group-hover:scale-105"
          unoptimized={slide.unoptimized}
        />
      </div>
      <h3 className="mt-2 truncate text-sm font-semibold">{slide.title}</h3>
    </>
  );

  if (!slide.href) return <div>{content}</div>;

  if (slide.isExternal) {
    return (
      <a
        href={slide.href}
        target="_blank"
        rel="noopener noreferrer"
        className="group block"
        data-analytics-event="banner_click"
        data-analytics-id={`homepage-destination-${slide.id}`}
        data-analytics-name={slide.title}
        data-analytics-type="link"
        data-analytics-funnel="hotel_booking"
        data-analytics-step="landing_destination"
        data-analytics-property-href={slide.href}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      href={slide.href}
      className="group block"
      data-analytics-event="banner_click"
      data-analytics-id={`homepage-destination-${slide.id}`}
      data-analytics-name={slide.title}
      data-analytics-type="link"
      data-analytics-funnel="hotel_booking"
      data-analytics-step="landing_destination"
      data-analytics-property-href={slide.href}
    >
      {content}
    </Link>
  );
}

function mapBannerToHeroSlide(
  banner: BannerItem,
  device: DeviceType,
): HeroSlide | null {
  const image = getBannerImage(banner, device);

  if (!image) return null;

  const link = getBannerLink(banner);

  return {
    id: banner.id,
    title: banner.alt ?? banner.title ?? "پیشنهاد ویژه",
    image,
    href: link?.href,
    isExternal: link?.isExternal,
    unoptimized: true,
  };
}

function getBannerImage(banner: BannerItem, device: DeviceType) {
  const { images } = banner;
  const variants =
    device === "desktop"
      ? [images.desktop, images.tablet, images.mobile]
      : device === "tablet"
        ? [images.tablet, images.desktop, images.mobile]
        : [images.mobile, images.tablet, images.desktop];

  return variants.find((image): image is string => Boolean(image?.trim()));
}

function OfferCountdown() {
  const [timeLeft, setTimeLeft] = useState("۰۸:۰۰:۰۰");
  const [timeParts, setTimeParts] = useState(["۰۸", "۰۰", "۰۰"]);

  useEffect(() => {
    const offerCycleMs = 8 * 60 * 60 * 1000;

    const updateTimer = () => {
      const remainingMs = offerCycleMs - (Date.now() % offerCycleMs);
      const totalSeconds = Math.floor(remainingMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const parts = [hours, minutes, seconds].map((value) =>
        toPersianDigits(value.toString().padStart(2, "0")),
      );

      setTimeParts(parts);
      setTimeLeft(parts.join(":"));
    };

    updateTimer();
    const interval = window.setInterval(updateTimer, 1000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <div className="flex w-full items-center justify-between gap-3 rounded-sm bg-red-600 px-3 py-2 text-white shadow-1 md:w-auto md:min-w-80 md:gap-5">
      <span className="text-xs font-semibold md:text-sm">
        پایان این پیشنهادها
      </span>
      <span dir="ltr" className="sr-only">
        {timeLeft}
      </span>
      <div dir="ltr" className="flex items-center gap-1">
        {timeParts.map((part, index) => (
          <span key={`${part}-${index}`} className="flex items-center gap-1">
            <span className="min-w-8 rounded-sm bg-white px-1.5 py-1 text-center text-base font-black tabular-nums text-red-600 shadow-sm md:min-w-10 md:text-lg">
              {part}
            </span>
            {index < timeParts.length - 1 && (
              <span className="font-black text-white/90">:</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function DiscountHotelCard({ hotel }: { hotel: HotelPromoHotel }) {
  const discountLabel =
    hotel.discount_label ??
    (hotel.discount_percent ? `${hotel.discount_percent}% تخفیف` : null);

  return (
    <Link
      href={hotel.to}
      className="group block overflow-hidden rounded-sm border border-red-100 bg-background text-foreground shadow-1"
      data-analytics-event="hotel_result_clicked"
      data-analytics-id={`homepage-discount-hotel-${hotel.id}`}
      data-analytics-name={hotel.name}
      data-analytics-type="link"
      data-analytics-funnel="hotel_booking"
      data-analytics-step="landing_discount_hotel"
      data-analytics-property-hotel-id={String(hotel.id)}
      data-analytics-property-hotel-name={hotel.name}
      data-analytics-property-href={hotel.to}
    >
      <div className="relative h-32 overflow-hidden sm:h-40 md:h-60">
        <Image
          src={hotel.image}
          alt={hotel.name}
          fill
          sizes="(max-width: 640px) 40vw, (max-width: 1024px) 40vw, 25vw"
          className="object-cover transition duration-300 group-hover:scale-105"
        />
        {(discountLabel || hotel.city) && (
          <div className="absolute inset-x-2 top-2 flex items-center justify-between gap-1.5 md:inset-x-3 md:top-3 md:gap-2">
            {discountLabel && (
              <span className="rounded-sm bg-red-600 px-2 py-1.5 text-[11px] font-black text-white shadow-1 md:px-3 md:text-sm">
                {discountLabel}
              </span>
            )}
            {hotel.city && (
              <span className="rounded-sm bg-black/55 px-1.5 py-1 text-[10px] font-medium text-white backdrop-blur md:px-2.5 md:text-xs">
                {hotel.city}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="p-2.5 md:p-3">
        <h3 className="truncate text-xs font-bold md:text-base">
          {hotel.name}
        </h3>
        <p className="mt-1 truncate text-[10px] text-foreground/60 md:text-xs">
          {hotel.address}
        </p>
        <div className="mt-2 rounded-sm bg-primary/10 px-2 py-1.5 md:mt-3 md:px-3 md:py-2">
          <span className="block text-[10px] font-medium text-foreground/60 md:text-xs">
            شروع قیمت از
          </span>
          <span className="mt-0.5 block truncate text-sm font-black text-red-600 md:text-lg">
            {hotel.nightly_price ?? hotel.price_label ?? "تماس بگیرید"}
          </span>
          <span className="mt-0.5 block text-[10px] text-foreground/50">
            برای یک شب
          </span>
        </div>
      </div>
    </Link>
  );
}

function toPersianDigits(value: string) {
  const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];

  return value.replace(/\d/g, (digit) => persianDigits[Number(digit)]);
}

function FeatureBanner({ banner }: { banner: HeroSlide }) {
  const { title, image, href } = banner;
  const content = (
    <Image
      src={image}
      alt={title}
      fill
      sizes="(max-width: 1024px) 50vw, 25vw"
      className="object-cover transition duration-300 group-hover:scale-105"
      unoptimized={banner.unoptimized}
    />
  );

  if (!href) {
    return (
      <div className="relative block aspect-square overflow-hidden rounded-sm shadow-1">
        {content}
      </div>
    );
  }

  return (
    <Link
      href={href}
      className="group relative block aspect-square overflow-hidden rounded-sm shadow-1"
      data-analytics-event="banner_click"
      data-analytics-id={`homepage-feature-banner-${slugifyAnalyticsId(title)}`}
      data-analytics-name={title}
      data-analytics-type="link"
      data-analytics-funnel="hotel_booking"
      data-analytics-step="landing_feature_banner"
      data-analytics-property-href={href}
    >
      {content}
    </Link>
  );
}

function slugifyAnalyticsId(value: string) {
  return value
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\u0600-\u06FF\w-]/g, "")
    .slice(0, 80);
}
